import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReceiptText, Trash2 } from 'lucide-react';
import { gatingSpecMismatches } from '../../discovery/discoveryModel';
import { Spinner, TruncatedText } from '../../ui';
import { OutreachAction, SupplierThread, ThreadMessage } from './types';
import { changeRibbonGroups } from '../../../hooks/outreachActionModel';
import EmptyThreadBody from './EmptyThreadBody';
import MessageBubble from './MessageBubble';
import MissingEmailBanner from './MissingEmailBanner';
import SpecMismatchBanner from './SpecMismatchBanner';
import QuoteStrip from './QuoteStrip';
import ChangeRibbon, { ExtractionFailedRibbon } from './ChangeRibbon';
import MovesBar, { MovesOverlay } from './MovesBar';
import ThreadStatusPill from './ThreadStatusPill';
import ThreadComposer from './ThreadComposer';
import { useReadOnly } from '../../../contexts/WizardAccessContext';

/**
 * Outreach conversation pane: supplier header, the one-line quote read-out, the
 * messages with the change ribbon sitting where each change happened, and
 * Robbie's moves compressed to a single bar above the composer.
 *
 * The conversation is what this pane is for, so it is the only thing that
 * grows: the quote is one line, the ribbon scrolls WITH the messages that
 * caused it rather than pinning itself above the composer, and the moves bar
 * lifts its detail OVER the messages instead of pushing them.
 *
 * A thread with no messages inverts that trade, because there is no
 * conversation to protect: the moves take the empty body at full size
 * (EmptyThreadBody) and the compact bar is not rendered at all. The first
 * message flips it back — permanently, since a thread never empties again.
 *
 * The buyer writes QUERIES, never emails: sending a query is the approval, and
 * Robbie composes and dispatches the email from it in the same round-trip.
 * Moves only ever fill this composer, so that send button is the one place an
 * email leaves from.
 *
 * A viewer gets the conversation and the quote read-out in full, and none of
 * the ways to add to it: no composer, no remove, no contact-email fix.
 */
interface ThreadChatProps {
  activeThread: SupplierThread;
  actions: OutreachAction[];
  /** The server's word that Robbie is still writing this thread's first asks. */
  kickoffPending?: boolean;
  /** The kickoff has been pending past its timeout. */
  kickoffTimedOut?: boolean;
  onRetryKickoff?: () => void;
  messageInput: string;
  onMessageInputChange: (val: string) => void;
  /** Sends the typed query now — Robbie writes the email from it. */
  onSendMessage: () => void;
  /** Writes these asks into the composer — one card's or several. Sends nothing. */
  onStageActions: (actions: OutreachAction[], approvedPrice?: number) => void;
  /** Ids whose asks are already in the composer, waiting on the buyer's send. */
  stagedActionIds: string[];
  onDismissAction: (actionId: string) => void;
  /** A send is in flight on THIS thread: Robbie is composing and dispatching. */
  isSending: boolean;
  /** Sends a "Not sent" bubble again. */
  onRetrySend?: (message: ThreadMessage) => void;
  sendError: string | null;
  onDismissSendError: () => void;
  onSaveContactEmail: (email: string) => Promise<boolean>;
  isSavingEmail: boolean;
  emailSaveError: string | null;
  /** The buyer's answer to a supplier offering a different spec than the BOM. */
  onDecideSpec?: (decision: 'ASK_FOR_OURS' | 'ACCEPT_THEIRS') => Promise<boolean>;
  isSavingSpecDecision?: boolean;
  specDecisionError?: string | null;
  /** Removes this supplier from outreach — offered only before anything is sent. */
  onDiscardThread: () => void;
  isDiscardingThread: boolean;
  /** Which of the bar's overlays starts open — the /design gallery uses it. */
  initialOverlay?: MovesOverlay;
}

const ThreadChat: React.FC<ThreadChatProps> = ({
  activeThread,
  actions,
  kickoffPending = false,
  kickoffTimedOut = false,
  onRetryKickoff,
  messageInput,
  onMessageInputChange,
  onSendMessage,
  onStageActions,
  stagedActionIds,
  onDismissAction,
  isSending,
  onRetrySend,
  sendError,
  onDismissSendError,
  onSaveContactEmail,
  isSavingEmail,
  emailSaveError,
  onDecideSpec,
  isSavingSpecDecision = false,
  specDecisionError,
  onDiscardThread,
  isDiscardingThread,
  initialOverlay,
}) => {
  const readOnly = useReadOnly();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  // Two-step removal: first click arms, second confirms. Re-arms on thread switch.
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  // Bumped every time asks land in the box — the cursor goes back there once
  // the new text has rendered. Which asks are staged is the hook's to say.
  const [stageNonce, setStageNonce] = useState(0);
  const { hasContactEmail } = activeThread;
  const isUncontacted = activeThread.status === 'not_sent';
  const hasNoMessages = activeThread.messages.length === 0;

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [activeThread.id, activeThread.messages.length]);

  useEffect(() => {
    setConfirmingDiscard(false);
    setStageNonce(0);
  }, [activeThread.id]);

  useEffect(() => {
    const box = composerRef.current;
    if (!box || stageNonce === 0) return;
    box.focus();
    box.setSelectionRange(box.value.length, box.value.length);
    box.scrollTop = box.scrollHeight;
  }, [stageNonce]);

  const stageActions = (chosen: OutreachAction[], approvedPrice?: number) => {
    onStageActions(chosen, approvedPrice);
    setStageNonce((prev) => prev + 1);
  };

  const ribbons = useMemo(() => changeRibbonGroups(activeThread), [activeThread]);
  const trailingRibbons = ribbons.filter((group) => group.afterMessageId === null);
  const inquiryBits = [
    activeThread.componentName,
    activeThread.quantity != null ? `Qty ${activeThread.quantity}` : undefined,
    activeThread.targetPriceLabel ? `Target ${activeThread.targetPriceLabel}` : undefined,
  ].filter(Boolean);

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden max-md:min-h-[66vh]">
      {/* Thread header */}
      <div className="flex shrink-0 items-center gap-[12px] border-b border-line-divider-soft px-[16px] py-[9px]">
        <div className="min-w-0">
          <TruncatedText as="div" className="text-card-header text-ink" text={activeThread.supplierName} />
          <TruncatedText as="div" className="text-meta text-ink-muted" text={inquiryBits.join(' · ')} />
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-s2">
          {activeThread.quote ? (
            <span className="inline-flex items-center gap-s1 rounded-pill border border-success-border bg-success-bg px-s3 py-[4px] text-meta font-semibold text-success-ink">
              <ReceiptText className="h-3 w-3" /> Quote on file
            </span>
          ) : (
            // No quote yet: the state itself is the news, in the same words
            // and the same pill the thread row uses.
            <ThreadStatusPill status={activeThread.status} />
          )}
          {isUncontacted && !readOnly && (
            <button
              type="button"
              onClick={() => (confirmingDiscard ? onDiscardThread() : setConfirmingDiscard(true))}
              onBlur={() => setConfirmingDiscard(false)}
              disabled={isDiscardingThread}
              title="Nothing has been sent to this supplier — removing takes them off your shortlist (undo from Supplier Discovery)"
              className={`inline-flex items-center gap-s1 rounded-control border px-s3 py-[4px] text-meta font-semibold disabled:opacity-50 ${
                confirmingDiscard
                  ? 'border-danger-border bg-danger-bg text-danger-ink'
                  : 'border-line bg-surface text-ink-secondary hover:border-danger-border hover:text-danger-ink'
              }`}
            >
              {isDiscardingThread ? <Spinner size="sm" /> : <Trash2 className="h-3 w-3" />}
              {isDiscardingThread ? 'Removing…' : confirmingDiscard ? 'Confirm remove?' : 'Remove supplier'}
            </button>
          )}
        </div>
      </div>

      {!hasContactEmail && !readOnly && (
        <MissingEmailBanner
          threadId={activeThread.id}
          onSave={onSaveContactEmail}
          isSaving={isSavingEmail}
          error={emailSaveError}
        />
      )}

      {gatingSpecMismatches(activeThread.specMismatches).length > 0 && (
        <SpecMismatchBanner
          mismatches={gatingSpecMismatches(activeThread.specMismatches)}
          decision={activeThread.specDecision ?? null}
          onDecide={readOnly ? undefined : onDecideSpec}
          isSaving={isSavingSpecDecision}
          error={specDecisionError ?? null}
        />
      )}

      <QuoteStrip thread={activeThread} />

      {/* Messages, with each ribbon under the reply that caused it — or, before
          there are any, Robbie's opening asks filling the space they leave */}
      <div ref={messagesContainerRef} className="flex min-h-0 flex-1 flex-col gap-s3 overflow-y-auto px-[16px] py-s3">
        {hasNoMessages ? (
          <EmptyThreadBody
            thread={activeThread}
            actions={actions}
            kickoffPending={kickoffPending}
            kickoffTimedOut={kickoffTimedOut}
            onRetryKickoff={onRetryKickoff}
            stagedIds={stagedActionIds}
            onStage={stageActions}
            onDismiss={onDismissAction}
            busy={isSending}
          />
        ) : (
          <>
            {activeThread.messages.map((msg) => (
              <React.Fragment key={msg.id}>
                <MessageBubble message={msg} supplierName={activeThread.supplierName} onRetry={onRetrySend} />
                {ribbons
                  .filter((group) => group.afterMessageId === msg.id)
                  .map((group) => (
                    <ChangeRibbon key={`${msg.id}-ribbon`} group={group} />
                  ))}
              </React.Fragment>
            ))}
            {trailingRibbons.map((group, idx) => (
              <ChangeRibbon key={`trailing-${idx}`} group={group} />
            ))}
            {activeThread.extractionStatus === 'FAILED' && <ExtractionFailedRibbon />}
          </>
        )}
      </div>

      {!hasNoMessages && (
        <MovesBar
          thread={activeThread}
          actions={actions}
          onStage={stageActions}
          onDismiss={onDismissAction}
          stagedIds={stagedActionIds}
          busy={isSending}
          initialOverlay={initialOverlay}
        />
      )}

      {/* Composer — a viewer has nothing to send, so it is not drawn at all. */}
      {!readOnly && (
        <ThreadComposer
          supplierName={activeThread.supplierName}
          hasContactEmail={hasContactEmail}
          value={messageInput}
          onChange={onMessageInputChange}
          onSend={onSendMessage}
          isSending={isSending}
          sendError={sendError}
          onDismissSendError={onDismissSendError}
          composerRef={composerRef}
        />
      )}
    </div>
  );
};

export default ThreadChat;
